/**
 * Single source of truth for "something is wrong" screens — 404s, runtime
 * exceptions, and the global error boundary all render this.
 *
 * It deliberately doesn't depend on the AppShell layout (no sidebar, no
 * topbar) because some failure modes (auth bootstrap explodes, root
 * provider crashes) prevent the shell from rendering. Keep this component
 * dependency-light: tailwind classes + lucide + the router's <Link>.
 */
import { useEffect, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertOctagon,
  ArrowLeft,
  Compass,
  Home,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/cn';

export type ErrorPageVariant = 'not-found' | 'error' | 'forbidden';

export interface ErrorPageProps {
  variant?: ErrorPageVariant;
  /** Override the headline. */
  title?: string;
  /** Override the supporting message. */
  description?: ReactNode;
  /** Display the URL the user was trying to reach. */
  requestedPath?: string;
  /** Show details of the underlying error (collapsible). */
  error?: Error | { message: string; stack?: string } | null;
  /** When provided, renders a "Try again" button calling this handler. */
  onRetry?: () => void;
  /** Hide the default "Go to dashboard" link (e.g. on login). */
  hideHome?: boolean;
  /**
   * Render without any router dependency. The global ErrorBoundary sits
   * OUTSIDE RouterProvider — a TanStack <Link> there throws inside the
   * boundary's own fallback and white-screens the app. With `routerless`
   * the home button is a plain full-page anchor instead.
   */
  routerless?: boolean;
}

const VARIANT_META: Record<
  ErrorPageVariant,
  { code: string; title: string; description: string; icon: typeof Compass; tone: string }
> = {
  'not-found': {
    code: '404',
    title: "We couldn't find that page",
    description:
      "The link may be broken, the page may have moved, or you don't have access to it.",
    icon: Compass,
    tone: 'text-primary',
  },
  error: {
    code: '500',
    title: 'Something went wrong',
    description:
      'An unexpected error happened on our end. Try again — if it keeps failing, check the details below and contact support.',
    icon: AlertOctagon,
    tone: 'text-destructive',
  },
  forbidden: {
    code: '403',
    title: "You don't have access to that",
    description:
      'Your role in this workspace doesn\'t include permission for that page. Ask an owner or admin to upgrade your role.',
    icon: AlertOctagon,
    tone: 'text-warning',
  },
};

export function ErrorPage({
  variant = 'error',
  title,
  description,
  requestedPath,
  error,
  onRetry,
  hideHome = false,
  routerless = false,
}: ErrorPageProps): JSX.Element {
  const meta = VARIANT_META[variant];
  const Icon = meta.icon;
  const headline = title ?? meta.title;
  const message = description ?? meta.description;

  // Developers can still inspect the underlying error via the console; we
  // never render the raw message or stack in the UI because it may contain
  // stack traces, database error text, or other technical noise.
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.debug('[ErrorPage]', error);
    }
  }, [error]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12">
      {/* Soft ambient glow so this doesn't look like a default browser page. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'radial-gradient(900px 500px at 20% 10%, hsl(var(--primary) / 0.08), transparent 60%),' +
            'radial-gradient(700px 400px at 80% 80%, hsl(var(--accent-violet) / 0.08), transparent 60%)',
        }}
      />

      <div className="relative w-full max-w-lg">
        <div className="rounded-2xl border border-border bg-card p-7 text-center shadow-elevation-md sm:p-10">
          <div
            className={cn(
              'mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-border/70 bg-muted/40',
              meta.tone,
            )}
          >
            <Icon className="h-6 w-6" aria-hidden />
          </div>

          <div className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Error · {meta.code}
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            {headline}
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            {message}
          </p>

          {requestedPath ? (
            <div className="mx-auto mt-4 inline-flex max-w-full items-center gap-2 rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-xs text-muted-foreground">
              <Search className="h-3 w-3 shrink-0" />
              <span className="truncate font-mono">{requestedPath}</span>
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </button>
            ) : null}
            {!hideHome ? (
              routerless ? (
                <a
                  href="/"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <Home className="h-4 w-4" />
                  Go to dashboard
                </a>
              ) : (
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <Home className="h-4 w-4" />
                  Go to dashboard
                </Link>
              )
            ) : null}
            <button
              type="button"
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              <ArrowLeft className="h-4 w-4" />
              Go back
            </button>
          </div>

          {/* Raw error message and stack are intentionally not shown here.
              Developers can inspect them via the browser console. */}
        </div>

        <div className="mt-6 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="h-3 w-3" /> SEO AI Agent
        </div>
      </div>
    </div>
  );
}
